import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatEUR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Package, ShoppingBag, BadgePercent, ArrowRight, MapPin, Loader2, Check, Shield } from "lucide-react";
import DeliveryAddressPicker from "../components/DeliveryAddressPicker";
import { toast } from "sonner";

const Dashboard = () => {
  const { user, setUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [addr, setAddr] = useState({
    address: user?.default_address || "",
    lat: user?.default_address_lat || null,
    lng: user?.default_address_lng || null,
  });
  const [saving, setSaving] = useState(false);
  const [promoting, setPromoting] = useState(false);

  const ADMIN_ALLOWLIST = ["easyicecalabria@gmail.com"];
  const canPromote = !!(user?.email && ADMIN_ALLOWLIST.includes(user.email) && user?.role !== "admin");

  const promoteSelf = async () => {
    setPromoting(true);
    try {
      await api.post("/auth/promote-self");
      const r = await api.get("/auth/me");
      setUser(r.data);
      toast.success("Sei ora amministratore", { description: "Ricarica la pagina se non vedi subito il menu Admin." });
    } catch (e) {
      toast.error("Errore", { description: e?.response?.data?.detail || "Riprova." });
    } finally { setPromoting(false); }
  };

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  useEffect(() => {
    setAddr({
      address: user?.default_address || "",
      lat: user?.default_address_lat || null,
      lng: user?.default_address_lng || null,
    });
  }, [user?.default_address, user?.default_address_lat, user?.default_address_lng]);

  const saveAddress = async () => {
    if (!addr.address || !addr.lat || !addr.lng) {
      toast.error("Seleziona un indirizzo dai suggerimenti o trascina il pin sulla mappa");
      return;
    }
    setSaving(true);
    try {
      const r = await api.put("/auth/profile", {
        default_address: addr.address,
        default_address_lat: addr.lat,
        default_address_lng: addr.lng,
      });
      setUser(r.data);
      toast.success("Indirizzo di consegna salvato");
    } catch (e) {
      toast.error("Errore", { description: e?.response?.data?.detail || "Riprova." });
    } finally {
      setSaving(false);
    }
  };

  const customCount = products.filter((p) => p.has_custom_price).length;
  const paid = orders.filter((o) => o.status === "paid").length;
  const hasAddress = !!(user?.default_address && user?.default_address_lat);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="dashboard-page">
      <p className="text-xs tracking-[0.2em] uppercase font-bold text-sky-600 mb-3">Dashboard</p>
      <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-light text-[#032B44]">
        Ciao, <span className="font-medium">{user?.name?.split(" ")[0]}</span> 👋
      </h1>
      <p className="text-slate-600 mt-3">Bentornato. Ecco un riepilogo del tuo account.</p>

      {user?.role === "staff" && (
        <div className="mt-8 bg-gradient-to-r from-sky-50 to-cyan-50 border border-sky-200 rounded-2xl p-5 flex items-start gap-4 flex-wrap" data-testid="staff-panel-banner">
          <div className="w-10 h-10 rounded-lg bg-sky-100 grid place-items-center shrink-0">
            <Shield className="w-5 h-5 text-sky-700" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <p className="font-heading text-base font-semibold text-sky-900">Pannello Operatore</p>
            <p className="text-sm text-sky-800 mt-0.5">
              Hai accesso a ordini e consegne. Apri il pannello per gestirli.
            </p>
          </div>
          <Link
            to="/admin"
            className="h-10 px-5 rounded-lg bg-[#032B44] hover:bg-[#00A3FF] text-white text-sm font-medium transition-colors inline-flex items-center gap-2"
            data-testid="staff-open-panel-button"
          >
            <Shield className="w-4 h-4" /> Apri pannello
          </Link>
        </div>
      )}

      {canPromote && (
        <div className="mt-8 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-4 flex-wrap" data-testid="promote-self-banner">
          <div className="w-10 h-10 rounded-lg bg-amber-100 grid place-items-center shrink-0">
            <Shield className="w-5 h-5 text-amber-700" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <p className="font-heading text-base font-semibold text-amber-900">Account amministratore</p>
            <p className="text-sm text-amber-800 mt-0.5">
              La tua email è autorizzata come admin. Clicca per attivare la dashboard amministratore.
            </p>
          </div>
          <button
            onClick={promoteSelf}
            disabled={promoting}
            className="h-10 px-5 rounded-lg bg-[#032B44] hover:bg-[#00A3FF] text-white text-sm font-medium transition-colors inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="promote-self-button"
          >
            {promoting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Shield className="w-4 h-4" /> Diventa admin</>}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-12">
        <Card icon={<ShoppingBag />} label="Ordini totali" value={orders.length} />
        <Card icon={<Package />} label="Ordini pagati" value={paid} />
        <Card icon={<BadgePercent />} label="Prezzi personalizzati" value={customCount} accent />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-12">
        <section className="bg-white border border-sky-100 rounded-2xl p-7">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading text-xl font-medium text-[#032B44]">Ultimi ordini</h2>
            <Link to="/orders" className="text-sm text-[#00A3FF] inline-flex items-center gap-1">Tutti <ArrowRight className="w-3 h-3" /></Link>
          </div>
          {orders.length === 0 ? (
            <p className="text-sm text-slate-500">Non hai ancora effettuato ordini.</p>
          ) : (
            <ul className="divide-y divide-sky-50">
              {orders.slice(0, 5).map((o) => (
                <li key={o.order_id} className="py-3 flex items-center justify-between text-sm">
                  <div>
                    <p className="font-medium text-[#032B44]">#{o.order_id.slice(-6)}</p>
                    <p className="text-xs text-slate-500">{new Date(o.created_at).toLocaleDateString("it-IT")} · {o.items.length} articoli</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-[#032B44]">{formatEUR(o.total)}</p>
                    <StatusBadge status={o.status} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="bg-gradient-to-br from-[#032B44] to-[#00A3FF] rounded-2xl p-7 text-white">
          <p className="text-xs tracking-[0.2em] uppercase font-bold opacity-80 mb-3">I tuoi vantaggi</p>
          <h3 className="font-heading text-2xl font-medium mb-4">Listino personalizzato attivo</h3>
          <p className="text-sm opacity-90 leading-relaxed">
            {customCount > 0
              ? `Hai ${customCount} prezzi dedicati. I prezzi personalizzati appaiono automaticamente nel catalogo e nel checkout.`
              : "Contattaci per attivare un listino personalizzato. I prezzi dedicati saranno visibili automaticamente."}
          </p>
          <Link to="/catalog" className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-lg bg-white text-[#032B44] text-sm font-medium hover:bg-sky-50 transition-colors" data-testid="dashboard-shop-button">
            Vai al catalogo <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      </div>

      <section className="bg-white border border-sky-100 rounded-2xl p-7 mt-12" data-testid="delivery-address-section">
        <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-sky-50 grid place-items-center shrink-0">
              <MapPin className="w-5 h-5 text-sky-700" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-medium text-[#032B44]">Indirizzo di consegna</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                {hasAddress
                  ? "Indirizzo predefinito per i tuoi ordini. Puoi modificarlo quando vuoi."
                  : "Salva il tuo indirizzo una volta: lo useremo per tutte le prossime consegne."}
              </p>
            </div>
          </div>
          {hasAddress && (
            <span className="inline-flex items-center gap-1 text-[11px] tracking-wider uppercase font-bold px-2.5 py-1 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">
              <Check className="w-3 h-3" /> Salvato
            </span>
          )}
        </div>
        <DeliveryAddressPicker value={addr} onChange={setAddr} />
        <div className="mt-4 flex justify-end">
          <button
            onClick={saveAddress}
            disabled={saving}
            className="inline-flex items-center gap-2 h-11 px-6 rounded-lg bg-[#032B44] hover:bg-[#00A3FF] text-white text-sm font-medium transition-colors disabled:opacity-50"
            data-testid="save-delivery-address-button"
          >
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Salvataggio…</> : "Salva indirizzo"}
          </button>
        </div>
      </section>
    </div>
  );
};

const Card = ({ icon, label, value, accent }) => (
  <div className={`rounded-2xl border p-6 ${accent ? "border-sky-200 bg-sky-50" : "border-sky-100 bg-white"}`}>
    <div className={`w-10 h-10 rounded-lg grid place-items-center mb-4 ${accent ? "bg-[#00A3FF] text-white" : "bg-sky-50 text-sky-700"}`}>
      {React.cloneElement(icon, { className: "w-5 h-5" })}
    </div>
    <p className="text-xs tracking-[0.2em] uppercase font-bold text-sky-600">{label}</p>
    <p className="font-heading text-3xl font-semibold text-[#032B44] mt-1">{value}</p>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    cancelled: "bg-slate-100 text-slate-600 border-slate-200",
  };
  const lbl = { paid: "Pagato", pending: "In attesa", cancelled: "Annullato" }[status] || status;
  return <span className={`text-[10px] tracking-wider uppercase font-bold px-2 py-0.5 rounded-full border ${map[status]}`}>{lbl}</span>;
};

export default Dashboard;
